// Phase 18 · Community · Post card (feed entry)
import React from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Hash, ShoppingBag, Layers, Video, Link2, Sparkles } from "lucide-react";
import VoteButtons from "./VoteButtons";
import MarketplaceEmbedCard from "./MarketplaceEmbedCard";
import MiniLandingEmbedCard from "./MiniLandingEmbedCard";
import VideoEmbed from "./VideoEmbed";

const TYPE_ICONS = {
    text: <Sparkles size={12} />,
    discussion: <MessageCircle size={12} />,
    case_study: <Layers size={12} />,
    image: <Layers size={12} />,
    video: <Video size={12} />,
    embed: <Video size={12} />,
    link: <Link2 size={12} />,
    asset_marketplace: <ShoppingBag size={12} />,
    mini_landing: <Layers size={12} />,
};

function fmtRelative(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return `${Math.floor(diff)}s ago`;
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return `${Math.floor(diff / 86400)}d ago`;
    } catch { return iso.slice(0, 10); }
}

export default function PostCard({ post, compact = false }) {
    if (!post) return null;
    // Phase 23a bonus · null-safety: legacy seed rows may have post_type=null
    // or title=null; render with safe fallbacks instead of crashing.
    const safeType = post.post_type || "text";
    const safeTitle = post.title || "(no title)";
    const safeBody = post.body || post.content || "";
    const isAI = post.ai_generated === true || post.origin === "autonomous_ai";
    return (
        <article
            className="group relative bg-[#111111] border border-[#1A1A1A] rounded-xl overflow-hidden hover:border-[#FFFFFF]/30 transition-all"
            data-testid={`post-card-${post.id}`}
        >
            <div className="flex gap-3 p-4">
                <VoteButtons targetType="post" targetId={post.id} initial={post} size="sm" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs text-neutral-500 mb-1.5">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#1A1A1A] text-neutral-300">
                            {TYPE_ICONS[safeType] || <Sparkles size={12} />}
                            {(safeType || "").replace("_", " ") || "post"}
                        </span>
                        {isAI && (
                            <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-[#FFFFFF]/20 to-[#CCCCCC]/20 border border-[#CCCCCC]/40 text-[#CCCCCC] font-semibold"
                                data-testid={`post-ai-badge-${post.id}`}
                                title="This post was generated autonomously by an AI virtual creator"
                            >
                                🤖 AI Content
                            </span>
                        )}
                        {post.category_slug && (
                            <Link
                                to={`/community/${post.category_slug}`}
                                className="inline-flex items-center gap-0.5 hover:text-[#FFFFFF] transition-colors"
                            >
                                <Hash size={10} />
                                {post.category_slug}
                            </Link>
                        )}
                        <span>·</span>
                        <Link
                            to={`/profile/${post.author_username || "unknown"}`}
                            className="hover:text-[#CCCCCC] transition-colors"
                        >
                            @{post.author_username || "unknown"}
                        </Link>
                        <span>·</span>
                        <span>{fmtRelative(post.created_at)}</span>
                    </div>
                    <Link
                        to={`/community/post/${post.id}`}
                        className="block group-hover:text-[#FFFFFF] transition-colors"
                        data-testid={`post-title-link-${post.id}`}
                    >
                        <h3 className="text-lg font-semibold text-neutral-100 leading-tight">
                            {safeTitle}
                        </h3>
                    </Link>
                    {!compact && safeBody && (
                        <p className="mt-2 text-sm text-neutral-400 line-clamp-3">
                            {safeBody}
                        </p>
                    )}
                    {/* wrapper previews */}
                    {safeType === "asset_marketplace" && post.marketplace_preview && (
                        <div className="mt-3">
                            <MarketplaceEmbedCard preview={post.marketplace_preview} />
                        </div>
                    )}
                    {safeType === "mini_landing" && post.mini_landing_preview && (
                        <div className="mt-3">
                            <MiniLandingEmbedCard preview={post.mini_landing_preview} />
                        </div>
                    )}
                    {(safeType === "video" || safeType === "embed") && post.embed_url && (
                        <div className="mt-3">
                            <VideoEmbed url={post.embed_url} />
                        </div>
                    )}
                    <div className="mt-3 flex items-center gap-3 text-xs text-neutral-500">
                        <Link
                            to={`/community/post/${post.id}`}
                            className="inline-flex items-center gap-1.5 hover:text-neutral-300 transition-colors"
                        >
                            <MessageCircle size={13} />
                            {post.comment_count || 0} comments
                        </Link>
                        <span>·</span>
                        <span>{post.view_count || 0} views</span>
                    </div>
                </div>
            </div>
        </article>
    );
}
